import * as React from "react";
import { Suspense } from "react";
import {
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  Dashboard as DashboardIcon,
  Inventory2 as Inventory2Icon,
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  HowToReg as HowToRegIcon,
  CloudUpload as CloudUploadIcon,
  AccountBalance as AccountBalanceIcon,
  Contacts as ContactsIcon,
  ReceiptLong as ReceiptLongIcon,
  Folder as FolderIcon,
  Api as ApiIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CardMembership as CardMembershipIcon,
  Payments as PaymentsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  DeleteOutline as DeleteOutlineIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  Autorenew as AutorenewIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  People as PeopleIcon,
  PointOfSale as PointOfSaleIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  NotificationsNone as NotificationsNoneIcon,
} from "@mui/icons-material";
import DescriptionIcon from '@mui/icons-material/Description';
import "../Layouts/Layout.Module.css";

import { useGetLoggedInUser } from "./Hooks";
import axios from "axios";
import { GetTokenFunc } from "../../utils/GetToken";
import { useContext } from "react";
import { ThemeContext } from "../../ThemeContext";

import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "./AdminHeader";
import { CircularProgress, Collapse } from "@mui/material";
import {
  ADMIN_SIDEBAR_ROUTES,
  getDefaultAdminPath,
  getDerivedActiveTokens,
  getFilteredAdminRoutes,
  isAdminPathAllowed,
} from "./adminSidebarRoutes";
import { clearAdminPortalType, resolveAdminPortalType } from "../../utils/adminPortal";

const drawerWidth = {
  xs: 240,
  sm: 240,
  md: 250,
};

const SideBar = () => {
  const muiTheme = useTheme();
  const { darkMode } = useContext(ThemeContext);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const palette = muiTheme.palette;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openMenus, setOpenMenus] = React.useState({});
  const location = useLocation();
  const pathnameLower = location.pathname.toLowerCase();
  const searchLower = location.search.toLowerCase();
  const derivedActiveTokens = React.useMemo(
    () => getDerivedActiveTokens(pathnameLower, searchLower),
    [pathnameLower, searchLower]
  );

  const pathMatchesMenuToken = React.useCallback(
    (token) => {
      const t = String(token).toLowerCase();
      if (!t) return false;
      return pathnameLower.includes(t) || derivedActiveTokens.includes(t);
    },
    [pathnameLower, derivedActiveTokens]
  );

  const routeMatchesActive = React.useCallback(
    (tokens) => Array.isArray(tokens) && tokens.some(pathMatchesMenuToken),
    [pathMatchesMenuToken]
  );

  const handleSubmenuToggle = (id) => {
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigate = useNavigate();
  const {
    data: CompanyData,
    isLoading: CompanyDataLoading,
    error: CompanyDataError,
  } = useGetLoggedInUser();

  const adminType = resolveAdminPortalType(CompanyData?.adminType);
  const userAccess = CompanyData?.acess;

  const redirectToDefaultRoute = React.useCallback(() => {
    navigate(getDefaultAdminPath(adminType, userAccess), { replace: true });
  }, [adminType, userAccess, navigate]);

  React.useEffect(() => {
    if (CompanyDataLoading || !CompanyData) return;
    if (isAdminPathAllowed(location.pathname, adminType, userAccess)) return;
    redirectToDefaultRoute();
  }, [
    CompanyData,
    CompanyDataLoading,
    location.pathname,
    adminType,
    userAccess,
    redirectToDefaultRoute,
  ]);

  React.useEffect(() => {
    if (adminType !== "brand_world") return;
    const p = location.pathname.toLowerCase();
    if (p.includes("/trash") || p.includes("/producttrash")) {
      redirectToDefaultRoute();
    }
  }, [adminType, location.pathname, redirectToDefaultRoute]);

  React.useEffect(() => {
    if (adminType !== "bxi") return;
    const p = location.pathname.toLowerCase();
    if (p.includes("bxi_marketplace")) return;

    const segments = location.pathname.split("/").filter(Boolean);
    const lastSeg = (segments[segments.length - 1] || "").toLowerCase();
    if (lastSeg === "inventory") {
      redirectToDefaultRoute();
      return;
    }

    const bxiForbiddenSnippets = [
      "/bwwallet",
      "/bxiwallet",
      "/bw-marketplace",
      "/allrecords",
      "/genie",
      "/helpandfeedback",
      "/tdsgst",
      "/bulkuploadapproval",
      "/notifications",
      "/needs-attention",
      "/ledger-reconciliation",
      "/notification-monitor",
    ];
    if (bxiForbiddenSnippets.some((s) => p.includes(s))) {
      redirectToDefaultRoute();
    }
  }, [adminType, location.pathname, redirectToDefaultRoute]);

  React.useEffect(() => {
    const p = pathnameLower;
    // Don't auto-open the Members submenu when the company was opened from Leads.
    const fromLeads = searchLower.includes("from=leads");
    if (
      p.includes("members") ||
      (p.includes("companylanding") && !fromLeads) ||
      p.includes("memberdirectory") ||
      p.includes("companyinvoicedetail") ||
      p.includes("memamountdebitstatus") ||
      (p.includes("einvoiceview") && /\/einvoiceview\/[^/]+\/mem(?:\/|$)/.test(p))
    ) {
      setOpenMenus((prev) => ({ ...prev, 102: true }));
    }
    if (
      p.includes("transactions") ||
      p.includes("/orders") ||
      p.includes("orderrequest") ||
      p.includes("performainvoice") ||
      p.includes("taxinvoice") ||
      p.includes("choose-transport") ||
      p.includes("commissionbyorderid")
    ) {
      setOpenMenus((prev) => ({ ...prev, 103: true }));
    }
    if (
      p.includes("products/") ||
      p.includes("bxi_marketplace") ||
      p.includes("marketplace/") ||
      p.includes("approvallisting") ||
      p.includes("allproductapprovals")
    ) {
      setOpenMenus((prev) => ({ ...prev, 104: true }));
    }
    if (
      p.includes("offline-payments") ||
      p.includes("bw-beneficiaries") ||
      p.includes("bw-payment-settings")
    ) {
      setOpenMenus((prev) => ({ ...prev, 123: true }));
    }
  }, [pathnameLower, searchLower]);

  const routeIconsById = {
    1: DashboardIcon,
    101: HowToRegIcon,
    102: ContactsIcon,
    103: ReceiptLongIcon,
    104: StoreIcon,
    105: WarehouseIcon,
    106: AdminPanelSettingsIcon,
    107: DescriptionIcon,
    17: AccountBalanceWalletIcon,
    2: StoreIcon,
    6: ReceiptLongIcon,
    8: FolderIcon,
    14: DescriptionIcon,
    7: EmailIcon,
    108: AdminPanelSettingsIcon,
    16: ReceiptIcon,
    9: ApiIcon,
    15: DeleteOutlineIcon,
    109: NotificationsNoneIcon,
    123: AccountBalanceIcon,
  };

  const childIconsById = {
    "102a": PeopleIcon,
    "102b": AutorenewIcon,
    "103a": PointOfSaleIcon,
    "103b": ShoppingCartIcon,
    "104a": CheckCircleOutlineIcon,
    "104b": HourglassEmptyIcon,
    "104c": RemoveCircleOutlineIcon,
    "123a": PaymentsIcon,
    "123b": AccountBalanceIcon,
    "123c": AccountBalanceIcon,
  };

  const filteredRoutes = (CompanyDataLoading || !CompanyData
    ? []
    : getFilteredAdminRoutes(adminType, userAccess)
  )
    .filter((route) => !route.hidden)
    .map((route) => ({
    ...route,
    Icon: routeIconsById[route.id],
    children: route.children?.map((child) => ({
      ...child,
      Icon: childIconsById[child.id],
    })),
  }));

  filteredRoutes.push({
    id: filteredRoutes.length + 1,
    name: "Log Out",
    Icon: LogoutIcon,
    linkUrl: "",
    activeMenuFor: ["logout"],
  });

  const drawer = (
    <List
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        "::-webkit-scrollbar": { width: 3 },
        "::-webkit-scrollbar-track": { background: "transparent" },
        "::-webkit-scrollbar-thumb": {
          background: palette.divider,
          borderRadius: 3,
        },
        "::-webkit-scrollbar-thumb:hover": {
          background: palette.text.disabled,
        },
        padding: { xs: 2, sm: 2.5 },
        backgroundColor: 'palette.background.paper',
        color: palette.text.primary,
      }}
    >
      {filteredRoutes?.map((res) => {
        const isActive = routeMatchesActive(res?.activeMenuFor);
        const hasChildren = Array.isArray(res.children) && res.children.length > 0;
        const isOpen = !!openMenus[res.id];

        if (hasChildren) {
          const anyChildActive = res.children.some((child) =>
            routeMatchesActive(child?.activeMenuFor)
          );
          return (
            <React.Fragment key={res?.id}>
              <ListItem
                disablePadding
                sx={{
                  mb: 0.25,
                  mx: 0,
                  borderRadius: "10px",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: palette.action.hover,
                  },
                  transition: "background-color 0.15s ease",
                }}
              >
                <ListItemButton
                  onClick={() => handleSubmenuToggle(res.id)}
                  sx={{
                    borderRadius: "10px",
                    minHeight: 40,
                    px: 1.5,
                    py: 0.75,
                    alignItems: "center",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      mr: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: anyChildActive ? palette.text.primary : palette.text.secondary,
                    }}
                  >
                    {res.Icon ? (
                      <res.Icon sx={{ fontSize: 20 }} />
                    ) : (
                      <DashboardIcon sx={{ fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={res?.name}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                      noWrap: true,
                    }}
                    sx={{
                      "& .MuiTypography-root": {
                        fontSize: "12.5px",
                        fontWeight: anyChildActive ? 500 : 400,
                        color: anyChildActive ? palette.text.primary : palette.text.secondary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  {isOpen ? (
                    <ExpandLess sx={{ fontSize: 18, color: palette.text.secondary }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: palette.text.secondary }} />
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {res.children.map((child) => {
                    const isChildActive = routeMatchesActive(child?.activeMenuFor);
                    return (
                      <ListItem
                        key={child.id}
                        disablePadding
                        component={Link}
                        to={child.linkUrl}
                        onClick={() => { if (isMobile) handleDrawerToggle(); }}
                        sx={{
                          mb: 0.25,
                          mx: 0,
                          borderRadius: "10px",
                          backgroundColor: isChildActive ? '#001219' : "transparent",
                          "&:hover": {
                            backgroundColor: isChildActive ? palette.primary.dark : palette.action.hover,
                          },
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <ListItemButton
                          sx={{
                            borderRadius: "10px",
                            minHeight: 36,
                            pl: 5,
                            pr: 1.5,
                            py: 0.5,
                            alignItems: "center",
                            "&:hover": { backgroundColor: "transparent" },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 30,
                              mr: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: isChildActive ? palette.primary.contrastText : palette.text.secondary,
                            }}
                          >
                            {child.Icon ? (
                              <child.Icon sx={{ fontSize: 17 }} />
                            ) : (
                              <DashboardIcon sx={{ fontSize: 17 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.name}
                            sx={{
                              "& .MuiTypography-root": {
                                fontSize: "11.5px",
                                fontWeight: isChildActive ? 500 : 400,
                                color: isChildActive ? palette.primary.contrastText : palette.text.secondary,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        }

        return (
          <ListItem
            key={res?.id}
            className={res?.linkUrl === "null" ? "disabled-link" : ""}
            disablePadding
            component={Link}
            to={res?.linkUrl !== "null" ? res?.linkUrl : "#"}
            onClick={(e) => {
              if (res?.name === "Log Out") {
                e.preventDefault();
                if (window.confirm("Are you sure you want to logout?")) {
                  // SECURITY (VAPT Finding 1): revoke the server-side session so
                  // the admin token cannot be reused after logout. Clear local
                  // state and redirect regardless of the API result.
                  const adminToken = GetTokenFunc();
                  axios
                    .post(
                      "/api/v1/admin/auth/logout",
                      {},
                      adminToken
                        ? { headers: { Authorization: `Bearer ${adminToken}` } }
                        : undefined
                    )
                    .catch(() => {})
                    .finally(() => {
                      document.cookie =
                        "BearerToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                      try {
                        window.localStorage?.removeItem("admintoken");
                      } catch (e) {}
                      clearAdminPortalType();
                      window.location.href = "/login";
                    });
                }
              }
              if (isMobile) handleDrawerToggle();
            }}
            sx={{
              mb: 0.25,
              mx: 0,
              borderRadius: "10px",
              backgroundColor: isActive ? '#001219' : "transparent",
              "&:hover": {
                backgroundColor: isActive ? palette.primary.dark : palette.action.hover,
              },
              transition: "background-color 0.15s ease",
            }}
          >
            <ListItemButton
              sx={{
                borderRadius: "10px",
                minHeight: 40,
                px: 1.5,
                py: 0.75,
                alignItems: "center",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  mr: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isActive ? palette.primary.contrastText : palette.text.secondary,
                }}
              >
                {res.Icon ? (
                  <res.Icon sx={{ fontSize: 20 }} />
                ) : (
                  <DashboardIcon sx={{ fontSize: 20 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={res?.name}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: 500,
                  noWrap: true,
                }}
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "12.5px",
                    fontWeight: isActive ? 500 : 400,
                    color: isActive ? palette.primary.contrastText : palette.text.secondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AdminHeader onMenuClick={handleDrawerToggle} />
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <CssBaseline />

        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                width: drawerWidth.xs,
                boxSizing: "border-box",
                backgroundColor: palette.background.paper,
                color: palette.text.primary,
                borderRight: "none",
                boxShadow: "0 0 40px rgba(0,0,0,0.08)",
              },
            }}
          >
            {drawer}
          </Drawer>
        )}

        {/* Desktop Drawer */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                boxSizing: "border-box",
                position: "relative",
                height: "calc(100vh - 64px)",
                borderRight: `1px solid ${palette.divider}`,
                backgroundColor: palette.background.paper,
                color: palette.text.primary,
                transition: "width 0.15s ease",
              },
            }}
          >
            {drawer}
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: { xs: "calc(100vh - 56px)", md: "calc(100vh - 64px)" },
            overflow: "auto",
            p: { xs: 3, sm: 4, md: 5 },
            backgroundColor: palette.background.default,
            color: palette.text.primary,
          }}
        >
          <Suspense fallback={
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
              <CircularProgress />
            </Box>
          }>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

export default SideBar;